import { createContext, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import Cookies from 'js-cookie'
import { getUser } from '../services/api'

const Context = createContext()

const DEFAULT_COMPANY_NAME = 'Controle de Produção'

/**
 * O SSO devolve `subCompanies: [{ id, name }]`. As telas foram escritas sobre
 * `subCompanyId`, então o mapeamento acontece aqui — um lugar só.
 */
const toSubCompanyList = perfil =>
  (perfil.subCompanies || []).map(item => ({ subCompanyId: item.id, name: item.name }))

/**
 * `permissions/index.js` e `PrivateRoute` comparam `rolesName`. O SSO manda
 * um array de string, então normalizamos aqui em vez de espalhar a diferença.
 */
const toRoleObjects = perfil => (perfil.roles || []).map(rolesName => ({ rolesName }))

function AuthProvider({ children }) {
  const token = localStorage.getItem('token')

  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)
  const [user, setUser] = useState('')
  const [userId, setUserId] = useState('')
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [subCompany, setSubCompany] = useState([])
  const [company, setCompany] = useState({})
  const [userRoles, setUserRoles] = useState([])
  const [userRolesNew, setUserRolesNew] = useState([])
  const [permissions, setPermissions] = useState([])

  // O tema vinha de `Company/Get`, que não existe mais na API nova. Os valores
  // padrão permanecem como identidade visual do produto.
  const [colorPrimary] = useState('#3D5D84')
  const [colorSecondary] = useState('#1976d2')
  const [colorMenu] = useState('#F1F9FF')
  const [letterPrimary] = useState('#212121')
  const [letterSecondary] = useState('#212121')
  const [fontColorMenuPrimary] = useState('#212121')
  const [fontColorMenuSecondary] = useState('#FFFFFF')
  const [sizeLogoMenu] = useState('180')
  const [urlLogo] = useState('')
  const [faviconLink] = useState('/favicon.ico')
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY_NAME)

  const applyPerfil = useCallback(perfil => {
    setUser(perfil)
    setUserId(perfil.userId || '')
    setUserName(perfil.fullName || '')
    setUserEmail(perfil.email || '')
    setSubCompany(toSubCompanyList(perfil))
    setCompany({ companyId: perfil.companyId || '', name: perfil.companyName || '' })
    setCompanyName(perfil.companyName || DEFAULT_COMPANY_NAME)
    setUserRoles(perfil.roles || [])
    setUserRolesNew(toRoleObjects(perfil))
    setPermissions(perfil.permissions || [])
    setAuthenticated(true)
  }, [])

  /** Recebe a resposta de `POST /auth/login` (`{ token, user }`). */
  const handleLogin = async response => {
    const data = response.data
    localStorage.setItem('token', data.token)
    applyPerfil(data.user)
  }

  const removeAllCookies = () => {
    const cookies = Cookies.get()
    Object.keys(cookies).forEach(cookieName => Cookies.remove(cookieName))
  }

  const handleLogout = useCallback(async () => {
    setCompanyName(DEFAULT_COMPANY_NAME)
    localStorage.removeItem('token')
    removeAllCookies()
    setUser('')
    setUserId('')
    setUserName('')
    setUserEmail('')
    setSubCompany([])
    setCompany({})
    setUserRoles([])
    setUserRolesNew([])
    setPermissions([])
    setAuthenticated(false)
  }, [])

  /** Sessão restaurada pelo perfil do SSO (`GET /auth/me`). */
  const getToken = useCallback(async () => {
    if (!token) {
      setLoading(false)
      return
    }
    try {
      const response = await getUser()
      applyPerfil(response.data)
    } catch (error) {
      setAuthenticated(false)
    } finally {
      setLoading(false)
    }
  }, [token, applyPerfil])

  useEffect(() => {
    getToken()
  }, [getToken])

  return (
    <Context.Provider
      value={{
        loading,
        authenticated,
        handleLogin,
        handleLogout,
        user,
        userId,
        userName,
        userEmail,
        subCompany,
        company,
        userRoles,
        userRolesNew,
        permissions,
        colorPrimary,
        colorSecondary,
        colorMenu,
        letterPrimary,
        letterSecondary,
        fontColorMenuPrimary,
        fontColorMenuSecondary,
        sizeLogoMenu,
        urlLogo,
        companyName,
      }}
    >
      <Helmet>
        <title>
          {companyName === DEFAULT_COMPANY_NAME
            ? companyName
            : `${companyName} - Controle de Produção`}
        </title>
        <link id="favicon" rel="icon" type="image/x-icon" sizes="16x16" href={faviconLink} />
      </Helmet>
      {children}
    </Context.Provider>
  )
}

export { Context, AuthProvider }
